import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Badge } from "../../../components/ui";
import { useVehicles } from "../hooks/useVehicles";
import { useMxLabels } from "../hooks/useMxLabels";
import { useMaintenanceRecords } from "../hooks/useMaintenanceRecords";
import { useMaintenanceActions } from "../hooks/useMaintenanceActions";
import { useMaintenanceCapabilities } from "../hooks/useMaintenanceCapabilities";
import { fetchAppointments } from "../services/maintenanceService";
import { APPT_STATUS_TONES, CHART_AXIS, CHART_COLORS, MAINTENANCE_STATUSES, MODE_CHART_COLORS, STATUS_CHART_COLORS } from "../theme";
import { costByCategory, costByMonth, costByVehicle, costOf, countBy, fmtMoney, fmtMonth, fmtTime, isDueWithin, isOpen, isOverdue, lastMonths, monthKey, priorityList, serviceDateOf, thumbOf, vehiclesInShop } from "../lib";
import type { MaintenanceAppointment } from "../types";
import { DueText, ErrorBlock, KpiCard, PageShell, Panel, StatusBadge, ToolbarButton, TruncationNotice } from "../components/kit/Primitives";
import { VehicleThumb } from "../components/kit/Media";
import { MaintenanceDrawer } from "../components/kit/MaintenanceDrawer";
import { MANTENIMIENTO_BASE } from "../paths";

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Resumen: tablero operativo del taller. KPIs reales del API (vencidos, esta
// semana, en taller, costo del mes y disponibilidad de la flota), gráficas de
// costo y estado, lista priorizada con foto, unidades en taller, agenda de hoy
// y los rankings de costo por categoría, unidad y taller.
export default function ResumenSection() {
  const L = useMxLabels();
  const { t } = L;
  const navigate = useNavigate();
  const { capabilities } = useMaintenanceCapabilities();
  const { vehicles, vehicleOf } = useVehicles();
  const { rows, total, loading, loadingMore, error, reload, loadMore } = useMaintenanceRecords();
  const actions = useMaintenanceActions(reload);
  const [appts, setAppts] = useState<MaintenanceAppointment[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchAppointments({ date: todayKey() })
      .then((list) => alive && setAppts(list))
      .catch(() => alive && setAppts([]));
    return () => {
      alive = false;
    };
  }, [rows]);

  const go = (path: string) => navigate(`${MANTENIMIENTO_BASE}/${path}`);
  const money = (n: number, compact = false) => fmtMoney(n, L.locale, compact);

  const data = useMemo(() => {
    const now = Date.now();
    const thisMonth = monthKey(now);
    const shop = vehiclesInShop(rows);
    const doneMonth = rows.filter((r) => r.status === "completado" && monthKey(serviceDateOf(r)) === thisMonth);
    const months = lastMonths(6, now);
    const fleetTotal = Math.max(vehicles.length, new Set(rows.map((r) => r.vehicleId)).size);
    const byWorkshop = countBy(rows.filter(isOpen), (r) => r.workshopName || null);
    return {
      overdue: rows.filter((r) => isOverdue(r, now)).length,
      week: rows.filter((r) => isDueWithin(r, 7, now)).length,
      shop,
      monthCost: doneMonth.reduce((s, r) => s + costOf(r), 0),
      monthCount: doneMonth.length,
      fleetTotal,
      available: Math.max(0, fleetTotal - shop.size),
      byMonth: costByMonth(rows, months).map((m) => ({ ...m, label: fmtMonth(m.month, L.locale) })),
      byStatus: MAINTENANCE_STATUSES.map((s) => ({ key: s, name: L.status(s), value: rows.filter((r) => (r.status || "pendiente") === s).length })).filter((x) => x.value > 0),
      byCategory: costByCategory(rows).slice(0, 7).map((c) => ({ ...c, name: L.category(c.category) })),
      topVehicles: costByVehicle(rows).slice(0, 6),
      workshops: Object.entries(byWorkshop).sort((a, b) => b[1] - a[1]).slice(0, 6),
      priority: priorityList(rows, 7, now).slice(0, 8),
      inShop: rows.filter((r) => r.status === "en_proceso"),
    };
  }, [rows, vehicles.length, L]);

  const selected = rows.find((r) => r.id === selectedId) || null;
  const availability = data.fleetTotal ? Math.round((data.available / data.fleetTotal) * 100) : 100;
  const maxVehicleCost = data.topVehicles[0]?.cost || 1;
  const maxWorkshop = data.workshops[0]?.[1] || 1;

  return (
    <PageShell
      icon="ri-dashboard-3-line"
      title={t("mantenimiento.menu.resumen")}
      subtitle={t("mantenimiento.resumenSubtitle")}
      actions={<ToolbarButton icon="ri-refresh-line" label={t("mantenimiento.ui.refresh")} onClick={reload} spinning={loading} />}
    >
      {error ? <ErrorBlock message={error} onRetry={reload} /> : null}

      <TruncationNotice shown={rows.length} total={total} onLoadMore={loadMore} loading={loadingMore} />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard icon="ri-alarm-warning-line" tone="error" label={t("mantenimiento.ui.kpi.overdue")} value={data.overdue} hint={t("mantenimiento.ui.kpi.overdueHint")} loading={loading} onClick={() => go("seguimiento")} />
        <KpiCard icon="ri-calendar-event-line" tone="warning" label={t("mantenimiento.ui.kpi.thisWeek")} value={data.week} hint={t("mantenimiento.ui.kpi.thisWeekHint")} loading={loading} onClick={() => go("seguimiento")} />
        <KpiCard icon="ri-tools-line" tone="info" label={t("mantenimiento.ui.kpi.inShop")} value={data.inShop.length} hint={t("mantenimiento.ui.kpi.inShopHint", { count: data.shop.size })} loading={loading} onClick={() => go("ordenes")} />
        <KpiCard icon="ri-money-dollar-circle-line" tone="primary" label={t("mantenimiento.ui.kpi.monthCost")} value={money(data.monthCost)} hint={t("mantenimiento.ui.kpi.monthCostHint", { count: data.monthCount })} loading={loading} onClick={() => go("reportes")} />
        <KpiCard icon="ri-steering-2-line" tone="success" label={t("mantenimiento.ui.kpi.availability")} value={`${availability} %`} progress={availability} hint={t("mantenimiento.ui.kpi.availabilityHint", { available: data.available, total: data.fleetTotal })} loading={loading} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title={t("mantenimiento.ui.charts.costByMonth")} icon="ri-bar-chart-2-line" className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.byMonth} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} vertical={false} />
                <XAxis dataKey="label" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} />
                <YAxis tick={CHART_AXIS.tick} axisLine={false} tickLine={false} tickFormatter={(v) => money(Number(v), true)} width={64} />
                <Tooltip contentStyle={CHART_AXIS.tooltip} formatter={(v: number) => money(Number(v))} cursor={{ fill: "rgb(var(--c-subtle))" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar isAnimationActive={false} dataKey="propio" name={t("mantenimiento.ui.charts.own")} stackId="c" fill={MODE_CHART_COLORS.propio} radius={[0, 0, 0, 0]} />
                <Bar isAnimationActive={false} dataKey="externo" name={t("mantenimiento.ui.charts.external")} stackId="c" fill={MODE_CHART_COLORS.externo} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title={t("mantenimiento.ui.charts.byStatus")} icon="ri-pie-chart-2-line">
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie isAnimationActive={false} data={data.byStatus} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2} stroke="none">
                  {data.byStatus.map((s) => (
                    <Cell key={s.key} fill={STATUS_CHART_COLORS[s.key]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={CHART_AXIS.tooltip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel
          title={t("mantenimiento.ui.priority")}
          icon="ri-alarm-warning-line"
          className="xl:col-span-2"
          bodyClassName="p-0"
          actions={<button type="button" onClick={() => go("seguimiento")} className="text-xs text-primary-500 hover:underline">{t("mantenimiento.ui.viewAll")}</button>}
        >
          <p className="border-b border-line px-4 py-2 text-[11px] text-content-muted">{t("mantenimiento.ui.priorityHint")}</p>
          {data.priority.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-content-muted">{loading ? t("mantenimiento.loading") : t("mantenimiento.ui.allClear")}</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.priority.map((r) => {
                const v = vehicleOf(r.vehicleId);
                return (
                  <li key={r.id}>
                    <button type="button" onClick={() => setSelectedId(r.id)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-subtle/50">
                      <VehicleThumb vehicle={v} size="md" badge={isOverdue(r) ? "alert" : null} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-content">{r.maintenanceDescription}</p>
                        <p className="truncate text-xs text-content-secondary">
                          {v?.label ?? r.vehicleId} · {r.workshopName || L.mode(r.workshopMode)}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusBadge record={r} size="sm" />
                        <DueText record={r} />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel title={t("mantenimiento.ui.inShopNow")} icon="ri-tools-line" actions={<span className="text-xs text-content-muted">{data.inShop.length}</span>}>
            {data.inShop.length === 0 ? (
              <p className="py-4 text-center text-xs text-content-muted">{t("mantenimiento.ui.nothingInShop")}</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {data.inShop.slice(0, 6).map((r) => {
                  const v = vehicleOf(r.vehicleId);
                  return (
                    <button key={r.id} type="button" onClick={() => setSelectedId(r.id)} className="group relative h-24 overflow-hidden rounded-lg text-left ring-1 ring-line">
                      {v?.image ? <img src={thumbOf(v.image)} alt={v.label} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" /> : <div className="flex h-full items-center justify-center bg-subtle text-content-faint"><i className="ri-truck-line text-2xl" /></div>}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute bottom-1.5 left-2 right-2">
                        <p className="truncate text-[11px] font-semibold text-white">{v?.label ?? r.vehicleId}</p>
                        <p className="truncate text-[10px] text-white/75">{r.maintenanceDescription}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Panel>
          <Panel title={t("mantenimiento.ui.todayAgenda")} icon="ri-calendar-check-line" actions={<button type="button" onClick={() => go("agenda")} className="text-xs text-primary-500 hover:underline">{t("mantenimiento.ui.viewAll")}</button>} bodyClassName="p-0">
            {appts.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-content-muted">{t("mantenimiento.ui.noAppointmentsToday")}</p>
            ) : (
              <ul className="max-h-64 divide-y divide-line overflow-y-auto">
                {[...appts].sort((a, b) => a.start.localeCompare(b.start)).map((a) => {
                  const v = vehicleOf(a.vehicleId);
                  return (
                    <li key={a.id} className="flex items-center gap-2.5 px-4 py-2">
                      <span className="w-[4.5rem] shrink-0 whitespace-nowrap text-xs font-semibold tabular-nums text-content">{fmtTime(a.start, L.locale)}</span>
                      <VehicleThumb vehicle={v} size="xs" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium text-content">{a.serviceName || t("mantenimiento.agenda.service")}</p>
                        <p className="truncate text-[11px] text-content-muted">{v?.label ?? a.vehicleId}{a.bay ? ` · ${t("mantenimiento.ui.agenda.bay", { n: a.bay })}` : ""}</p>
                      </div>
                      <Badge tone={APPT_STATUS_TONES[a.status] ?? "neutral"} size="sm">{L.apptStatus(a.status)}</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel title={t("mantenimiento.ui.charts.byCategory")} icon="ri-price-tag-3-line">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.byCategory} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} horizontal={false} />
                <XAxis type="number" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} tickFormatter={(v) => money(Number(v), true)} />
                <YAxis type="category" dataKey="name" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} width={92} />
                <Tooltip contentStyle={CHART_AXIS.tooltip} formatter={(v: number) => money(Number(v))} cursor={{ fill: "rgb(var(--c-subtle))" }} />
                <Bar isAnimationActive={false} dataKey="cost" name={t("mantenimiento.ui.charts.cost")} radius={[0, 4, 4, 0]}>
                  {data.byCategory.map((c, i) => (
                    <Cell key={c.category} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title={t("mantenimiento.ui.charts.topVehicles")} icon="ri-truck-line" actions={<button type="button" onClick={() => go("historial")} className="text-xs text-primary-500 hover:underline">{t("mantenimiento.ui.viewAll")}</button>}>
          <ul className="space-y-2.5">
            {data.topVehicles.map((x) => {
              const v = vehicleOf(x.vehicleId);
              return (
                <li key={x.vehicleId}>
                  <button type="button" onClick={() => navigate(`${MANTENIMIENTO_BASE}/historial?vehicle=${encodeURIComponent(x.vehicleId)}`)} className="flex w-full items-center gap-2.5 text-left">
                    <VehicleThumb vehicle={v} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-medium text-content">{v?.label ?? x.vehicleId}</span>
                        <span className="shrink-0 text-xs font-semibold tabular-nums text-content">{money(x.cost, true)}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-subtle">
                        <div className="h-full rounded-full bg-primary-500" style={{ width: `${(x.cost / maxVehicleCost) * 100}%` }} />
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </Panel>
        <Panel title={t("mantenimiento.ui.charts.workshopLoad")} icon="ri-store-2-line" actions={<button type="button" onClick={() => go("talleres")} className="text-xs text-primary-500 hover:underline">{t("mantenimiento.ui.viewAll")}</button>}>
          <ul className="space-y-3">
            {data.workshops.map(([name, count]) => (
              <li key={name}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate text-content">{name}</span>
                  <span className="shrink-0 font-semibold tabular-nums text-content">{count}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-subtle">
                  <div className="h-full rounded-full bg-warning" style={{ width: `${(count / maxWorkshop) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {selected ? (
        <MaintenanceDrawer
          record={selected}
          vehicle={vehicleOf(selected.vehicleId)}
          onClose={() => setSelectedId(null)}
          canManage={capabilities.canManageRecords}
          canCharge={capabilities.canCharge}
          onTransition={actions.transition}
          onPdf={actions.workOrderPdf}
          onCharge={actions.charge}
          onOpenVehicle={(id) => navigate(`${MANTENIMIENTO_BASE}/historial?vehicle=${encodeURIComponent(id)}`)}
        />
      ) : null}
    </PageShell>
  );
}
